// ============================================================================
// "Tell us about the building" — design reference screen 3, in the book's own
// words (Decision 4: sterile / non-sterile / total, the names buildings use).
// The ONE component that writes a bid's building fields (§15a). The follow-up
// questions show before "Continue"; answered ones go into the walkthrough
// notes as dated lines. Continue prices the bid (bid_price) and moves on.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../shared/components';
import { fetchShareBand, priceBid, updateBid, type BidRow } from './api';
import { followUps, notesWithAnswers, type FollowUp } from './followups';
import { Field, INPUT, SaveError } from './fields';
import { saveText } from './start-form';
import { num, parseNumber } from './format';

type K = 'num_or_suites' | 'num_procedure_rooms' | 'num_pre_op_bays' | 'total_sqft' | 'sterile_sqft' | 'non_sterile_sqft'
  | 'service_freq_per_week' | 'or_terminal_nights_per_week' | 'chair_count';

const KEYS: K[] = ['num_or_suites', 'num_procedure_rooms', 'num_pre_op_bays', 'total_sqft', 'sterile_sqft', 'non_sterile_sqft',
  'service_freq_per_week', 'or_terminal_nights_per_week', 'chair_count'];

function draftFrom(b: BidRow): Record<K, string> {
  const o = {} as Record<K, string>;
  for (const k of KEYS) o[k] = b[k] != null ? String(b[k]) : '';
  return o;
}

function NumberField({ label, value, onChange, hint, disabled, error }: { label: string; value: string; onChange: (v: string) => void; hint?: string; disabled?: boolean; error?: string | null }) {
  return (
    <Field label={label} hint={hint} error={error}>
      <input className={INPUT} inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
    </Field>
  );
}

export function BuildingForm({ bid, onPriced, locked }: { bid: BidRow; onPriced: () => void; locked?: boolean }) {
  const [d, setD] = useState<Record<K, string>>(() => draftFrom(bid));
  const [nsTouched, setNsTouched] = useState(bid.non_sterile_sqft != null);
  const [asked, setAsked] = useState<Record<string, { on: boolean; answer: string }>>({});
  const [band, setBand] = useState<{ or_band: string; low: number; high: number; n: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setD(draftFrom(bid)); setAsked({}); setNsTouched(bid.non_sterile_sqft != null); }, [bid.bid_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dialysis = bid.facility_type === 'Dialysis';
  const n = (k: K) => parseNumber(d[k]);
  const set = (k: K) => (v: string) => setD((x) => ({ ...x, [k]: v }));

  // non-sterile defaults to total − sterile until the BDD types their own
  const nsShown = nsTouched ? d.non_sterile_sqft : (n('total_sqft') != null && n('sterile_sqft') != null ? String(Math.max((n('total_sqft') as number) - (n('sterile_sqft') as number), 0)) : '');

  useEffect(() => {
    let cancelled = false;
    if (dialysis) { setBand(null); return; }
    fetchShareBand(bid.facility_type, n('num_or_suites'))
      .then((b) => { if (!cancelled) setBand(b); })
      .catch(() => { if (!cancelled) setBand(null); });
    return () => { cancelled = true; };
  }, [d.num_or_suites, bid.facility_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const questions: FollowUp[] = useMemo(
    () => followUps({ facility_type: bid.facility_type, num_or_suites: n('num_or_suites'), total_sqft: n('total_sqft'), sterile_sqft: n('sterile_sqft') }, band),
    [d, band, bid.facility_type], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const nights = n('service_freq_per_week');
  const orNights = n('or_terminal_nights_per_week');
  const problems: string[] = [];
  if (nights == null || nights < 1 || nights > 7) problems.push('How many nights a week is the building cleaned (1–7)?');
  if (dialysis) {
    if (n('total_sqft') == null && (n('chair_count') ?? 0) <= 0) problems.push('Give the chair count, or the square footage from the walkthrough.');
  } else {
    if (n('total_sqft') == null) problems.push('Give the total square footage.');
    if (n('sterile_sqft') == null) problems.push('Give the sterile square footage.');
    if (n('total_sqft') != null && n('sterile_sqft') != null && (n('sterile_sqft') as number) > (n('total_sqft') as number)) problems.push('Sterile cannot be more than the total.');
    if (orNights != null && nights != null && orNights > nights) problems.push('OR terminal nights cannot be more than the building nights.');
  }

  async function save() {
    setSaving(true);
    setErr(null);
    const fields: Record<string, number | string | null> = {};
    for (const k of KEYS) fields[k] = k === 'non_sterile_sqft' ? parseNumber(nsShown) : n(k);
    if (dialysis) { fields.sterile_sqft = null; fields.non_sterile_sqft = null; fields.num_or_suites = null; fields.num_procedure_rooms = null; fields.num_pre_op_bays = null; fields.or_terminal_nights_per_week = null; }
    else if (fields.or_terminal_nights_per_week == null) fields.or_terminal_nights_per_week = fields.service_freq_per_week;
    const answered = questions.filter((q) => asked[q.key]?.on).map((q) => ({ question: q.question, answer: asked[q.key]?.answer ?? '' }));
    if (answered.length) fields.walkthrough_notes = notesWithAnswers(bid.walkthrough_notes, answered, new Date().toISOString().slice(0, 10));
    try {
      await updateBid(bid.bid_id, fields, 'save the building');
      await priceBid(bid.bid_id);
      setAsked({});
      onPriced();
    } catch (e) {
      setErr(saveText(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <SaveError text={err} />
      {dialysis ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <NumberField label="Chairs" value={d.chair_count} onChange={set('chair_count')} disabled={locked} />
          <NumberField label="Total sqft" value={d.total_sqft} onChange={set('total_sqft')} disabled={locked}
            hint={d.total_sqft.trim() === '' ? 'Walkthrough pending? Leave it blank — the app estimates it from the chairs and labels it estimated.' : undefined} />
          <NumberField label="Nights cleaned a week" value={d.service_freq_per_week} onChange={set('service_freq_per_week')} disabled={locked} />
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <NumberField label="Operating rooms" value={d.num_or_suites} onChange={set('num_or_suites')} disabled={locked} />
            <NumberField label="Procedure rooms" value={d.num_procedure_rooms} onChange={set('num_procedure_rooms')} disabled={locked} />
            <NumberField label="Pre-op / PACU bays" value={d.num_pre_op_bays} onChange={set('num_pre_op_bays')} disabled={locked} />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <NumberField label="Total sqft" value={d.total_sqft} onChange={set('total_sqft')} disabled={locked} />
            <NumberField label="Sterile sqft" value={d.sterile_sqft} onChange={set('sterile_sqft')} disabled={locked} hint="ORs, sterile corridor, SPD." />
            <NumberField label="Non-sterile sqft" value={nsShown} onChange={(v) => { setNsTouched(true); set('non_sterile_sqft')(v); }} disabled={locked}
              hint={nsTouched ? undefined : 'Total minus sterile until you change it.'} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField label="Building nights a week" value={d.service_freq_per_week} onChange={set('service_freq_per_week')} disabled={locked} />
            <NumberField label="OR terminal-clean nights a week" value={d.or_terminal_nights_per_week} onChange={set('or_terminal_nights_per_week')} disabled={locked}
              hint="Blank means every building night." />
          </div>
        </>
      )}

      {!locked && questions.length > 0 && (
        <div className="rounded-lg border border-st-info-border bg-st-info-bg p-3">
          <div className="mb-2 text-sm font-semibold text-st-info-fg">Before this goes to pricing, ask:</div>
          <ul className="space-y-2">
            {questions.map((q) => (
              <li key={q.key} className="text-sm text-tcs-text">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={asked[q.key]?.on ?? false}
                    onChange={(e) => setAsked((a) => ({ ...a, [q.key]: { on: e.target.checked, answer: a[q.key]?.answer ?? '' } }))} />
                  <span>{q.question}</span>
                </label>
                {asked[q.key]?.on && (
                  <input className={`${INPUT} mt-1`} placeholder="What they said (optional)" value={asked[q.key]?.answer ?? ''}
                    onChange={(e) => setAsked((a) => ({ ...a, [q.key]: { on: true, answer: e.target.value } }))} />
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-tcs-text-muted">Ticked questions are saved to the walkthrough notes with today&apos;s date.</p>
        </div>
      )}

      {!locked && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {problems[0] && <span className="text-xs text-tcs-text-muted">{problems[0]}</span>}
          <Button onClick={save} disabled={problems.length > 0} loading={saving}>Continue to services</Button>
        </div>
      )}
      {locked && <p className="text-sm text-tcs-text-muted">{num(bid.total_sqft)} sf · the price is approved and locked.</p>}
    </div>
  );
}
